import React, { useEffect, useState, useCallback } from 'react';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { X, Loader2, Pencil, Trash2 } from 'lucide-react';

const EMPTY = { name: '', email: '', phone: '', birth_date: '', formula: 'permis_b', neph: '', hours_package: '', assigned_teacher_id: '', notes: '' };

export default function SecretariatElevesPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [formules, setFormules] = useState({});
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'create' | fiche
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const refresh = useCallback(() => {
    api.secEleves().then((d) => setItems(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
    api.secTeachers().then((d) => setTeachers(d.items || [])).catch(() => {});
    api.secFormules().then((d) => setFormules(d.formules || {})).catch(() => {});
  }, [authLoading, user, refresh]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const openCreate = () => { setForm(EMPTY); setErrMsg(''); setModal('create'); };
  const openEdit = (f) => { setForm({ ...f, hours_package: f.hours_package ?? '' }); setErrMsg(''); setModal(f); };

  const save = async () => {
    setSaving(true); setErrMsg('');
    const payload = { name: form.name, email: form.email, phone: form.phone, birth_date: form.birth_date, formula: form.formula, neph: form.neph || '', hours_package: form.hours_package === '' ? null : Number(form.hours_package), assigned_teacher_id: form.assigned_teacher_id || '', notes: form.notes || '' };
    try {
      if (modal === 'create') await api.secCreateEleve(payload);
      else await api.secUpdateEleve(form.id, payload);
      setModal(null); refresh();
    } catch (e) { setErrMsg(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer la fiche de ${f.name} (et ses leçons) ?`)) return;
    await api.secDeleteEleve(f.id).catch(() => {});
    refresh();
  };

  const teacherName = (id) => teachers.find((t) => t.id === id)?.name || '—';
  const filtered = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <SecretariatShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-eleves">
        <SectionTitle kicker="Secrétariat · Élèves" title="Fiches élèves" sub="Créez les fiches, choisissez la formule de permis et suivez le forfait d'heures." />
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un élève…" data-testid="sec-eleves-search" className="h-10 w-64 rounded-full bg-black/40 border border-white/15 px-4 text-white text-[13px]" />
          <button onClick={openCreate} data-testid="sec-eleve-create-btn" className="ml-auto h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[13px] font-bold">+ Nouvelle fiche élève</button>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/45 text-[13px]" data-testid="sec-eleves-empty">Aucune fiche élève — créez la première !</div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-x-auto">
            <table className="w-full min-w-[720px] text-[13px]">
              <thead><tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/8">
                <th className="text-left p-4">Élève</th><th className="text-left p-4">Formule</th><th className="text-left p-4">Heures</th><th className="text-left p-4">Enseignant réf.</th><th className="text-left p-4 hidden md:table-cell">Téléphone</th><th className="p-4"></th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((f) => {
                  const pct = f.hours_package ? Math.min(100, Math.round((f.hours_done / f.hours_package) * 100)) : 0;
                  return (
                    <tr key={f.id} data-testid={`sec-eleve-row-${f.id}`} className={f.status === 'archive' ? 'opacity-40' : ''}>
                      <td className="p-4"><div className="text-white font-semibold">{f.name}</div><div className="text-white/40 text-[11.5px]">{f.email}{f.neph ? <span className="ml-2 text-amber-400/70 font-mono-spec">NEPH {f.neph}</span> : ''}{f.account_linked === false ? <span className="ml-2 text-orange-400 font-semibold" title="Aucun compte utilisateur avec cet email — le planning de l'élève restera vide dans son espace." data-testid={`eleve-unlinked-${f.id}`}>⚠ compte non relié</span> : ''}</div></td>
                      <td className="p-4"><span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-bold">{formules[f.formula]?.label || f.formula}</span></td>
                      <td className="p-4 w-44">
                        <div className="text-white/75 text-[12px] mb-1">{f.hours_done} / {f.hours_package} h</div>
                        <div className="h-1.5 bg-white/8 rounded-full overflow-hidden"><div className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} /></div>
                      </td>
                      <td className="p-4 text-white/60">{teacherName(f.assigned_teacher_id)}</td>
                      <td className="p-4 text-white/50 hidden md:table-cell">{f.phone || '—'}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(f)} data-testid={`sec-eleve-edit-${f.id}`} className="text-white/50 hover:text-white p-1.5"><Pencil size={14} /></button>
                        <button onClick={() => remove(f)} className="text-red-400/60 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {modal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(null)}>
            <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#111113] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sec-eleve-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif-editorial text-white text-[20px]">{modal === 'create' ? 'Nouvelle fiche élève' : `Modifier ${form.name}`}</h3>
                <button onClick={() => setModal(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom et prénom *" data-testid="sec-eleve-name-input" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (lie son compte élève)" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                  <input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                </div>
                <label className="block text-[12px] text-white/55">Formule de permis
                  <select value={form.formula} onChange={(e) => setForm({ ...form, formula: e.target.value, hours_package: '' })} data-testid="sec-eleve-formula-select" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]">
                    {Object.entries(formules).map(([v, f]) => <option key={v} value={v}>{f.label} — {f.hours} h</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-[12px] text-white/55">Forfait d'heures
                    <input type="number" value={form.hours_package} onChange={(e) => setForm({ ...form, hours_package: e.target.value })} placeholder={`${formules[form.formula]?.hours || 20} (défaut)`} data-testid="sec-eleve-hours-input" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                  </label>
                  <label className="block text-[12px] text-white/55">Date de naissance
                    <input type="date" value={form.birth_date || ''} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                  </label>
                </div>
                <label className="block text-[12px] text-white/55">NEPH (n° d'enregistrement préfectoral — obligatoire DSR)
                  <input value={form.neph || ''} onChange={(e) => setForm({ ...form, neph: e.target.value })} placeholder="Ex : 123456789012" data-testid="sec-eleve-neph-input" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                </label>
                <label className="block text-[12px] text-white/55">Enseignant référent
                  <select value={form.assigned_teacher_id || ''} onChange={(e) => setForm({ ...form, assigned_teacher_id: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]">
                    <option value="">— Aucun —</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes internes" rows={2} className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white text-[13px]" />
                {errMsg && <div className="text-red-300 text-[12.5px]">⚠️ {String(errMsg)}</div>}
                <button onClick={save} disabled={saving || form.name.trim().length < 2} data-testid="sec-eleve-save-btn" className="w-full h-10 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[13px] font-bold inline-flex items-center justify-center gap-2">
                  {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </SecretariatShell>
  );
}
